import './Segment.css';

// Libraries
import React, { useRef, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import { v4 as uuidv4 } from 'uuid';

// Components
import Alert from 'components/Alert/Alert';
import FilterGroup from './FilterGroup/FilterGroup';
import Modal from 'components/Modal/Modal';
import ContentPanel from 'components/ContentPanel/ContentPanel';
import Dropdown from 'components/Form/Dropdown/Dropdown';
import DraggablePill from 'components/DraggablePill/DraggablePill';
import SegmentPreview from '../SegmentPreview/SegmentPreview';
import { CurrentPermissionContext } from 'components/AccessWrapper/AccessWrapper';
import { PermissionsContext } from 'App';
import ContentHeader from 'components/ContentHeader/ContentHeader';
import EditableHighlightField from 'components/EditableHighlightField/EditableHighlightField';
import SideMenu from 'components/SideMenu/SideMenu';
import Accordion from 'components/Accordion/Accordion';
import CardList from 'components/CardList/CardList';
import Button from 'components/Buttons/Button/Button';
import Loader from 'components/Loader/Loader';
import SegmentDragLayer from 'views/Segmentation/Segment/SegmentDragLayer/SegmentDragLayer';
import Tooltip from 'components/Tooltip/Tooltip';
import IconButton from 'components/Buttons/IconButton/IconButton';
import SelectTableJoinPathModal from 'components/Segment/SelectTableJoinPathModal/SelectTableJoinPathModal';
import DynamicAppLogoButton from 'components/DynamicAppFlowsButton/DynamicAppFlowsButton';

// Variables & Models
import { default as FilterGroupModel } from 'views/Segmentation/models/filterGroup';
import { DraggableItemTypes } from '../models/definitions';
import TableProperty from '../models/tableProperty';

// Utils & hooks
import { capFirst } from 'utils/text.util';
import {
    useQuery,
    useLazyQuery,
    GET_SEGMENT,
    IS_SEGMENT_NAME_VALID,
    DELETE_SEGMENT,
    updateSegment,
    createSegment,
    GET_CONSENTS_FOR_TABLE,
    GET_TABLES,
    useMutation,
    GET_SEGMENT_RESULTS_COUNT,
    GET_ARE_SEGMENT_RESULTS_RUNNING,
} from 'utils/graphql';
import { canCreate, canDelete, hasPermissionFor } from 'utils/permission.util';
import { useNavCollapse } from 'contexts/NavCollapseContext';
import { useFetch } from 'utils/rest/request';
import { loadQueryFilterGroup, getTableArrayFromTableTree } from 'utils/segment.util';
import { useTableTrees } from 'contexts/TableTreeContext';

// Assets
import { ReactComponent as ErrorIcon } from 'assets/icons/exclamation-triangle.svg';
import { ReactComponent as ExpandMoreIcon } from 'assets/icons/expand_more_24.svg';
import { ReactComponent as StarIcon } from 'assets/icons/star_icon_24.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete_icon.svg';
import { ReactComponent as PlayIcon } from 'assets/icons/play_icon_24.svg';
import { ReactComponent as BackIcon } from 'assets/icons/back_icon_24.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download_icon_24.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-triangle.svg';
import { ReactComponent as RouteIcon } from 'assets/icons/route_icon_24.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info_icon_48.svg';

const Segment = () => {
    let navigate = useNavigate();
    const location = useLocation();
    let { segment_id } = useParams();
    const navCollapsed = useNavCollapse();
    const segmentContainerRef = useRef(null);
    const segmentBuilderRef = useRef(null);
    const { permissionSet } = useContext(CurrentPermissionContext);
    const [segmentType, setSegmentType] = useState('segment');
    const [name, setName] = useState('');
    const [consents, setConsents] = useState();
    const [consent, setConsent] = useState();
    const [consentTableProperty, setConsentTableProperty] = useState();
    const [isConsentJoinPathModalOpen, setIsConsentJoinPathModalOpen] = useState(false);
    const [primaryTableId, setPrimaryTableId] = useState('');
    const [primaryTableTree, setPrimaryTableTree] = useState();
    const [includeFilterGroup, setIncludeFilterGroup] = useState(new FilterGroupModel(true));
    const [excludeFilterGroup, setExcludeFilterGroup] = useState(new FilterGroupModel(true));
    const [includeCollapsed, setIncludeCollapsed] = useState(false);
    const [excludeCollapsed, setExcludeCollapsed] = useState(true);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [modalType, setModalType] = useState();
    const [errorMsgs, setErrorMsgs] = useState();
    const [isPreview, setIsPreview] = useState(false);
    const [tablePropertyItemsForAccordion, setTablePropertyItemsForAccordion] = useState([]);
    const [loadingTableProperties, setLoadingTableProperties] = useState(true);
    const [showSaveMenu, setShowSaveMenu] = useState(false);
    const [tables, setTables] = useState([]);
    const [joinTables, setJoinTables] = useState([]);
    const [primaryTableSelected, setPrimaryTableSelected] = useState(false);
    const [callUpdateFilterGroupOnHover, setCallUpdateFilterGroupOnHover] = useState(false);
    const [triggerClearAllPlaceholders, setTriggerClearAllPlaceholders] = useState(false);
    const [isCombinePreviewActive, setIsCombinePreviewActive] = useState(false);
    const [isSegmentBuilderFixed, setIsSegmentBuilderFixed] = useState(false);
    const [isLoadingSegmentBuilder, setIsLoadingSegmentBuilder] = useState(false);
    const [segmentBuilderWidth, setSegmentBuilderWidth] = useState('0');
    const [segmentBuilderLeftPos, setSegmentBuilderLeftPos] = useState();
    const [resultsCount, setResultsCount] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [resultsRunning, setResultsRunning] = useState(true);
    const [shouldPoll, setShouldPoll] = useState(true);
    const [groupedFlows, setGroupedFlows] = useState(null);
    const { getTableTreeByGlobalId } = useTableTrees();
    const { permissions } = useContext(PermissionsContext);

    if (segment_id === 'new') {
        segment_id = null;
    }

    const [deleteSegment] = useMutation(DELETE_SEGMENT);

    const handleSegmentationResultsRunning = (data) => {
        const result = data?.segmentationAreResultsRunning;
        if (result && !result.error) {
            setResultsRunning(result.active);
            if (!result.active) {
                setShouldPoll(false);
            }
        }
    };

    const { refetch: refetchResultsRunning } = useQuery(GET_ARE_SEGMENT_RESULTS_RUNNING, {
        skip: !segment_id,
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
        variables: { segmentationId: segment_id },
        onCompleted: handleSegmentationResultsRunning,
    });

    const { data: segment, loading: isLoadingSegment } = useQuery(GET_SEGMENT, {
        skip: !segment_id,
        variables: { id: segment_id },
        fetchPolicy: 'no-cache',
    });

    const handleSegmentResultsCount = (data) => {
        const result = data?.segmentationResultsCount;
        if (result) {
            setResultsCount(result.count);
        }
    };

    const { refetch: refetchResultsCount } = useQuery(GET_SEGMENT_RESULTS_COUNT, {
        skip: !segment_id || resultsRunning,
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
        variables: { segmentationId: segment_id },
        onCompleted: handleSegmentResultsCount,
    });

    const { data: consentsData } = useQuery(GET_CONSENTS_FOR_TABLE, {
        fetchPolicy: 'no-cache',
        skip: !primaryTableId,
        variables: { id: primaryTableId },
    });

    const { data: tablesData } = useQuery(GET_TABLES, {
        variables: {
            offset: 0,
            limit: 99,
        },
        fetchPolicy: 'cache-and-network',
    });

    const {
        error: connected_apps_error,
        data: connected_apps_data,
        loading: apps_loading,
    } = useFetch('/api/connected_apps', {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 3000,
    });

    const {
        error: segment_app_routines_error,
        data: segment_app_routines_data,
        loading: segment_app_routines_loading,
    } = useFetch(`/api/connected_apps/tenant/routines?source_id=${segment_id}`, {
        skip: !segment_id,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 3000,
    });

    const [isSegmentationNameValid] = useLazyQuery(IS_SEGMENT_NAME_VALID);

    const openModal = (type) => {
        setModalType(type);
        setIsOpen(true);
    };

    const getModalOptions = (type) => {
        switch (type) {
            case 'delete':
                return {
                    type: 'confirmation',
                    title: 'Warning',
                    content: (
                        <div>
                            <p>Are you sure you want to delete this segment? </p>
                            <b>{name}</b>
                        </div>
                    ),
                    confirmBtnText: 'Yes',
                    cancelBtnText: 'No',
                    width: '250px',
                    textAlign: 'center',
                    confirmationBtnAction: async () => {
                        deleteSegment({
                            variables: { id: segment_id },
                            onCompleted: (data) => {
                                if (data.deleteSegmentation) {
                                    openModal('deleteSuccess');
                                    setTimeout(() => {
                                        navigate(`/segmentation`);
                                    }, 1000);
                                } else {
                                    openModal('deleteError');
                                }
                            },
                        });
                    },
                };
            case 'deleteError':
                return {
                    title: <WarningIcon fill="var(--error-color)" width="40" height="40" />,
                    content: (
                        <div>
                            <p>Segment could not be deleted.</p>
                        </div>
                    ),
                    width: '250px',
                    textAlign: 'center',
                };
            case 'deleteSuccess':
                return {
                    title: 'Success',
                    content: (
                        <div>
                            <p>{capFirst(segmentType)} deleted.</p>
                        </div>
                    ),
                    width: '250px',
                    textAlign: 'center',
                    confirmationBtnAction: () => {},
                };
            case 'cancel':
                return {
                    type: 'confirmation',
                    title: 'Are you sure?',
                    content: (
                        <div>
                            <p>All unsaved changes will be lost.</p>
                        </div>
                    ),
                    width: '250px',
                    textAlign: 'center',
                    confirmationBtnAction: () => {
                        const returnRoute = segmentType === 'segment' ? 'segmentation' : 'consent';
                        navigate(`/${returnRoute}`);
                    },
                };
            case 'save':
                return {
                    title: 'Success',
                    content: (
                        <div>
                            <p>{capFirst(segmentType)} saved.</p>
                        </div>
                    ),
                    width: '250px',
                    textAlign: 'center',
                    confirmationBtnAction: () => {},
                };
            case 'saveAs':
                return {
                    title: 'Success',
                    content: (
                        <div>
                            <p>New {capFirst(segmentType)} created.</p>
                        </div>
                    ),
                    width: '250px',
                    textAlign: 'center',
                    confirmationBtnAction: () => {},
                };
            case 'creationError':
                return {
                    title: <ErrorIcon fill="var(--error-color)" width="40" height="40" />,
                    content: <p>There was an error while creating the segment.</p>,
                    width: '250px',
                    textAlign: 'center',
                };
            case 'updateError':
                return {
                    title: <ErrorIcon fill="var(--error-color)" width="40" height="40" />,
                    content: <p>There was an error while updating the segment.</p>,
                    width: '250px',
                    textAlign: 'center',
                };
            case 'flowsLoading':
                return {
                    title: <InfoIcon fill="var(--warning-color)" width="40" height="40" />,
                    content: <p>The flow connections are loading.</p>,
                    width: '250px',
                    textAlign: 'center',
                };
            case 'flowsLoadingError':
                return {
                    title: <ErrorIcon fill="var(--error-color)" width="40" height="40" />,
                    content: <p>There was an error while loading the flow connections.</p>,
                    width: '250px',
                    textAlign: 'center',
                };
            default:
                return {};
        }
    };

    const showConsentDropdown = () => {
        return !isPreview && segmentType === 'segment' && consents?.length > 0;
    };

    const validate = async (isSaveAs = false) => {
        let _errorMsgs = [];
        if (includeFilterGroup.filterCount === 0 && excludeFilterGroup.filterCount === 0) {
            _errorMsgs = ['Filters cannot be empty.'];
        } else {
            const isIncludeFilterGroupValide = includeFilterGroup.validate();
            const isExcludeFilterGroupValide = excludeFilterGroup.validate();
            if (!isIncludeFilterGroupValide || !isExcludeFilterGroupValide) {
                _errorMsgs = ['Highlighted filters and filter groups are not valid.'];
            }
            // updating filter groups state
            updateFilterGroup(includeFilterGroup);
            updateFilterGroup(excludeFilterGroup);
        }
        if (includeFilterGroup.maxDepth > 5) {
            _errorMsgs.push('The maximum depth of 5 has been exceeded for the include filters.');
        }
        if (excludeFilterGroup.maxDepth > 5) {
            _errorMsgs.push('The maximum depth of 5 has been exceeded for the exclude filters.');
        }

        if (!name.trim()) {
            _errorMsgs.push(`${capFirst(segmentType)} name cannot be empty.`);
        } else if (isSaveAs && segment?.segmentation && segment.segmentation?.name === name) {
            _errorMsgs.push([`${capFirst(segmentType)} name already exists.`]);
        } else if (!(segment?.segmentation && segment.segmentation.name === name)) {
            const { data } = await isSegmentationNameValid({
                variables: { name },
            });
            if (!data.isSegmentationNameValid) {
                _errorMsgs.push(`${capFirst(segmentType)} name already exists.`);
            }
        }

        setErrorMsgs(_errorMsgs);
        return _errorMsgs.length === 0;
    };

    const togglePreview = async () => {
        if (await validate()) {
            setIsPreview((isPreview) => !isPreview);
        }
    };

    const onSaveClickHandler = async () => {
        if (await validate()) {
            if (segment_id) {
                setIsSaving(true);
                setResultsRunning(true);
                setShouldPoll(true);
                // UPDATE
                const segmentation = {
                    id: segment_id,
                    name: name,
                    primaryTableId: primaryTableId,
                    includeFilterGroup: includeFilterGroup,
                    excludeFilterGroup: excludeFilterGroup,
                    consentId: consent ? consent.global_id : null,
                    consentJoinPath:
                        consent && consentTableProperty?.joinPath
                            ? {
                                  path: consentTableProperty.joinPath,
                                  id: consentTableProperty.joinPathId,
                              }
                            : null,
                };
                updateSegment(segmentation)
                    .then((result) => {
                        setIsSaving(false);
                        if (result.errors) {
                            setResultsRunning(false);
                            openModal('updateError');
                        } else {
                            openModal('save');
                            // reset results count to show loader
                            setResultsCount(null);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                        setIsSaving(false);
                        setResultsRunning(false);
                        openModal('updateError');
                    });
            } else {
                createSegmentation();
            }
        }
    };

    const onSaveAsClickHandler = async () => {
        if (await validate(true)) {
            createSegmentation();
        }
    };

    const createSegmentation = () => {
        setIsSaving(true);
        const segmentation = {
            name: name,
            isConsent: segmentType === 'consent' ? true : false,
            primaryTableId: primaryTableId,
            includeFilterGroup: includeFilterGroup,
            excludeFilterGroup: excludeFilterGroup,
            consentId: consent ? consent.global_id : null,
            consentJoinPath:
                consent && consentTableProperty?.joinPath ? consentTableProperty.joinPath : null,
        };
        createSegment(segmentation)
            .then((result) => {
                setIsSaving(false);
                if (result.errors) {
                    openModal('creationError');
                } else {
                    openModal('saveAs');
                    setTimeout(() => {
                        const returnRoute = segmentType === 'segment' ? 'segmentation' : 'consent';
                        navigate(
                            `/${returnRoute}/` + result.data.createSegmentation.segmentation.id
                        );
                    }, 1000);
                }
            })
            .catch((e) => {
                console.error(e);
                setIsSaving(false);
                openModal('creationError');
            });
    };

    const updateFilterGroup = (
        type,
        { id, filters, operator, isPlaceholder, isCombining } = {}
    ) => {
        const filterGroup = type === 'include' ? includeFilterGroup : excludeFilterGroup;
        const otherFilterGroup = type === 'include' ? excludeFilterGroup : includeFilterGroup;
        id = id || filterGroup.id;
        filters = filters || filterGroup.filters;
        operator = operator || filterGroup.operator;
        isPlaceholder = isPlaceholder != null ? isPlaceholder : filterGroup.isPlaceholder;
        isCombining = isCombining != null ? isCombining : filterGroup.isCombining;

        const updatedFilterGroup = new FilterGroupModel(
            filterGroup.isRoot,
            operator,
            filters,
            filterGroup.isNew,
            isPlaceholder,
            isCombining,
            filterGroup.isValid,
            id
        );
        const otherUpdatedFilterGroup = new FilterGroupModel(
            otherFilterGroup.isRoot,
            otherFilterGroup.operator,
            filterOutAllChildPlaceholders(otherFilterGroup.filters),
            otherFilterGroup.isNew,
            otherFilterGroup.isPlaceholder,
            otherFilterGroup.isCombining,
            otherFilterGroup.isValid,
            otherFilterGroup.id
        );
        if (type === 'include') {
            setIncludeFilterGroup(updatedFilterGroup);
            setExcludeFilterGroup(otherUpdatedFilterGroup);
        } else {
            setExcludeFilterGroup(updatedFilterGroup);
            setIncludeFilterGroup(otherUpdatedFilterGroup);
        }
    };

    const filterOutAllChildPlaceholders = (filters) => {
        return update(filters, {
            $apply: (filters) =>
                filters
                    .filter((filter) => !filter.isPlaceholder)
                    .map((filter) => {
                        // if child filterGroup, recursively filter out child placeholders
                        if (!filter.isFilter) {
                            return update(filter, {
                                filters: { $set: filterOutAllChildPlaceholders(filter.filters) },
                            });
                        }
                        return filter;
                    }),
        });
    };

    const updateSegmentBuilderWidthAndPosition = () => {
        if (!segmentBuilderRef.current) return;
        setSegmentBuilderWidth(segmentBuilderRef.current.getBoundingClientRect().width);
        setSegmentBuilderLeftPos(segmentBuilderRef.current.getBoundingClientRect().left);
    };

    const updateSelectedConsent = (globalId) => {
        const consent = consents.find((consent) => consent.global_id === globalId);
        if (!consent) return;
        setConsent(consent);
        if (primaryTableId === consent.primary_table_global_id) {
            setConsentTableProperty(null);
        } else {
            setConsentTableProperty(
                new TableProperty(
                    {
                        id: uuidv4(),
                        property: 'id',
                        table: {
                            id: consent.primary_table,
                            global_id: consent.primary_table_global_id,
                        },
                        type: { type: 'UUID' },
                    },
                    primaryTableTree
                )
            );
        }
    };

    // load existing segmentation
    useEffect(() => {
        if (!segment?.segmentation) return;
        setName(segment?.segmentation.name);
        setPrimaryTableId(segment?.segmentation.primaryTable.id);
        setPrimaryTableSelected(true);
    }, [segment]);

    // load existing segmentation include and exclude filter groups
    useEffect(() => {
        if (!segment?.segmentation || !primaryTableTree) return;
        let _includeFilterGroup, _excludeFilterGroup;
        if (segment?.segmentation.includeFilterGroup) {
            _includeFilterGroup = loadQueryFilterGroup(
                segment?.segmentation.includeFilterGroup,
                primaryTableTree,
                true
            );
            setIncludeFilterGroup(_includeFilterGroup);
        } else {
            setIncludeCollapsed(true);
        }
        if (segment?.segmentation.excludeFilterGroup) {
            _excludeFilterGroup = loadQueryFilterGroup(
                segment?.segmentation.excludeFilterGroup,
                primaryTableTree,
                true
            );
            setExcludeFilterGroup(_excludeFilterGroup);
            setExcludeCollapsed(false);
        }
    }, [segment, primaryTableTree]);

    // load consent and consent join path
    useEffect(() => {
        if (!segment?.segmentation || !primaryTableTree || !consents) return;
        let _consentTableProperty = null;
        if (segment?.segmentation.consent) {
            const consent = consents.find(
                (consent) => consent.global_id === segment.segmentation.consent.id
            );
            setConsent(consent);
            const consentJoinPath = segment?.segmentation.consentJoinPath;
            _consentTableProperty = new TableProperty(
                {
                    id: uuidv4(),
                    property: 'id',
                    table: {
                        id: consent.primary_table,
                        global_id: consent.primary_table_global_id,
                    },
                    type: { type: 'UUID' },
                },
                primaryTableTree,
                consentJoinPath ? consentJoinPath.path : null,
                consentJoinPath ? consentJoinPath.id : null
            );
            setConsentTableProperty(_consentTableProperty);
        }
    }, [segment, primaryTableTree, consents]);

    useEffect(() => {
        const returnPath = location.pathname.split('/')[1];
        setSegmentType(returnPath === 'segmentation' ? 'segment' : 'consent');
    }, [location.pathname]);

    useEffect(() => {
        if (!tablesData || !tablesData.tableSchemaList) return;

        const _tables = tablesData.tableSchemaList.edges
            .filter((edge) => !edge.node.isJunction)
            .map((edge) => edge.node);
        setTables(_tables);
        if (!segment_id) {
            setPrimaryTableId(_tables[0]?.id);
        }
    }, [tablesData, segment_id]);

    useEffect(() => {
        if (!primaryTableId) return;
        setLoadingTableProperties(true);
    }, [primaryTableId]);

    useEffect(() => {
        if (!primaryTableId) return;
        async function setupJoinTables() {
            const tableTree = await getTableTreeByGlobalId(primaryTableId);
            if (!tableTree) {
                setLoadingTableProperties(false);
                return;
            }

            const propertyDragHandler = (item, monitor) => {
                // checking if the property was dropped outside the drop area
                if (!monitor.didDrop()) {
                    setIsCombinePreviewActive(false);
                    setTriggerClearAllPlaceholders((value) => !value);
                }
                if (!callUpdateFilterGroupOnHover) {
                    setCallUpdateFilterGroupOnHover(true);
                }
            };

            const accordianItems = [];
            const _tables = getTableArrayFromTableTree(tableTree);
            setJoinTables(_tables);
            for (let table of _tables) {
                // using the first property id and table so that the existing logic works
                const firstProperty = table.properties[0];
                const _properties = [...table.properties];
                if (primaryTableId !== table.global_id) {
                    _properties.push({
                        property: 'count',
                        id: `${firstProperty.id}_count`,
                        global_id: firstProperty.global_id,
                        type: {
                            type: 'Function',
                        },
                        table: { ...firstProperty.table },
                    });
                }

                accordianItems.push({
                    header: (
                        <div className="segment-table-header">
                            {table.name}{' '}
                            {table.global_id === primaryTableId && (
                                <Tooltip
                                    tip="Primary table. Segment results will be from this object."
                                    width="200px"
                                    position="right-center"
                                >
                                    <StarIcon fill="var(--accent-color)" />
                                </Tooltip>
                            )}
                        </div>
                    ),
                    body: (
                        <>
                            {_properties.map((property, i) => (
                                <DraggablePill
                                    type={DraggableItemTypes.PROPERTY}
                                    key={i}
                                    index={i}
                                    text={property.property}
                                    data={property}
                                    dragHandler={propertyDragHandler}
                                    dependencies={[property]}
                                />
                            ))}
                        </>
                    ),
                    classNameHeader:
                        table.global_id === primaryTableId ? 'primary-table-header' : '',
                });
            }
            setTablePropertyItemsForAccordion(accordianItems);
            setLoadingTableProperties(false);
            setPrimaryTableTree(tableTree);
        }
        setupJoinTables();
    }, [getTableTreeByGlobalId, primaryTableId, callUpdateFilterGroupOnHover]);

    useEffect(() => {
        if (!consentsData) return;
        setConsents(consentsData.consentList);
    }, [consentsData]);

    useEffect(() => {
        if (!primaryTableSelected || !segmentBuilderRef.current) return;

        const handleScrollAndResize = () => {
            if (!segmentBuilderRef.current) return;
            const fixedBreakPoint =
                window.scrollY + segmentBuilderRef.current.getBoundingClientRect().top;
            setIsSegmentBuilderFixed(window.scrollY >= fixedBreakPoint);
            updateSegmentBuilderWidthAndPosition();
        };
        updateSegmentBuilderWidthAndPosition();
        window.addEventListener('scroll', handleScrollAndResize, { passive: true });
        window.addEventListener('resize', updateSegmentBuilderWidthAndPosition);

        const resizeObserver = new ResizeObserver(handleScrollAndResize);
        if (segmentContainerRef.current) {
            resizeObserver.observe(segmentContainerRef.current);
        }

        // clean up event listeners and the resize observer
        return () => {
            window.removeEventListener('scroll', handleScrollAndResize);
            window.removeEventListener('resize', updateSegmentBuilderWidthAndPosition);
            resizeObserver.disconnect();
        };
    }, [primaryTableSelected]);

    useEffect(() => {
        if (!primaryTableSelected || !segmentBuilderRef.current) return;
        setIsLoadingSegmentBuilder(true);
        const timeOutId = setTimeout(() => {
            updateSegmentBuilderWidthAndPosition();
            setIsLoadingSegmentBuilder(false);
        }, 500);
        return () => clearTimeout(timeOutId);
    }, [navCollapsed, primaryTableSelected]);

    useEffect(() => {
        if (!segment_id || resultsRunning || resultsCount !== null) return;
        const pollInterval = setInterval(() => {
            refetchResultsCount({
                fetchPolicy: 'no-cache',
            }).then(({ data }) => handleSegmentResultsCount(data));
        }, 8000);

        return () => clearInterval(pollInterval);
    }, [segment_id, resultsRunning, resultsCount, refetchResultsCount]);

    useEffect(() => {
        if (!segment_id || isSaving || !shouldPoll) return;

        const interval = setInterval(() => {
            refetchResultsRunning({ fetchPolicy: 'no-cache' }).then(({ data }) => {
                handleSegmentationResultsRunning(data);
            });
        }, 8000);

        return () => clearInterval(interval);
    }, [segment_id, isSaving, shouldPoll, refetchResultsRunning]);

    useEffect(() => {
        if (!segment_app_routines_data || !connected_apps_data) return;
        const groupedRoutinesByApp = [];
        for (const app of connected_apps_data.rows) {
            for (const connection of app.connections) {
                const routines = segment_app_routines_data.rows?.filter(
                    (routine) => routine.connection.id === connection.id
                );

                if (routines.length > 0) {
                    groupedRoutinesByApp.push({
                        app: app,
                        connection: connection,
                        routines: routines,
                    });
                }
            }
        }
        setGroupedFlows(groupedRoutinesByApp);
    }, [segment_app_routines_data, connected_apps_data]);

    return (
        <div className="segment">
            {!primaryTableSelected && !segment_id ? (
                <ContentPanel title={'Select Primary Table'}>
                    <div className="segment-primary-table">
                        <Dropdown
                            value={primaryTableId}
                            values={tables.map((table) => ({ text: table.name, value: table.id }))}
                            width="100%"
                            setValue={(e) => {
                                setPrimaryTableId(e.target.value);
                            }}
                        />
                        <button
                            className="btn segment-primary-table-btn"
                            onClick={() => setPrimaryTableSelected(true)}
                        >
                            Continue
                        </button>
                    </div>
                </ContentPanel>
            ) : (
                <div ref={segmentContainerRef}>
                    <ContentHeader>
                        <div className="segment-header">
                            <div className="segment-name">
                                <EditableHighlightField
                                    defaultIsEditing={!segment_id}
                                    defaultDisplayName={`${capFirst(segmentType)} Name`}
                                    placeholder={`${capFirst(segmentType)} Name`}
                                    value={name}
                                    setValue={(value) => setName(value)}
                                />
                            </div>
                            <div className="segment-action-btns">
                                <div className="action-btn-group">
                                    {!isPreview && canCreate(permissionSet) && (
                                        <div style={{ position: 'relative' }}>
                                            <Button
                                                height="35px"
                                                onClick={() =>
                                                    segment_id
                                                        ? setShowSaveMenu(true)
                                                        : onSaveClickHandler()
                                                }
                                                isDisabled={
                                                    loadingTableProperties || isLoadingSegment
                                                }
                                                isLoading={isSaving}
                                            >
                                                Save
                                                {segment_id && <ExpandMoreIcon fill="#fff" />}
                                            </Button>
                                            <CardList
                                                items={[
                                                    {
                                                        content: 'Save',
                                                        clickCallBack: () => {
                                                            onSaveClickHandler();
                                                            setShowSaveMenu(false);
                                                        },
                                                    },
                                                    {
                                                        content: 'Save As',
                                                        clickCallBack: () => {
                                                            onSaveAsClickHandler();
                                                            setShowSaveMenu(false);
                                                        },
                                                    },
                                                ]}
                                                onClickOutside={() => setShowSaveMenu(false)}
                                                getCardItemContent={(item) => item.content}
                                                show={showSaveMenu}
                                                width="100px"
                                                top="40px"
                                                className="save-btn-list"
                                                left="50%"
                                                showDivider="all"
                                            />
                                        </div>
                                    )}
                                    {segment_id !== null && (
                                        <Button
                                            height="35px"
                                            width={isPreview ? '120px' : '100px'}
                                            onClick={togglePreview}
                                            isDisabled={
                                                loadingTableProperties ||
                                                isLoadingSegment ||
                                                resultsRunning
                                            }
                                            tooltip={
                                                resultsRunning ? 'Processing results...' : null
                                            }
                                        >
                                            {isPreview ? 'Back to Builder' : 'Preview'}
                                        </Button>
                                    )}

                                    {showConsentDropdown() && (
                                        <>
                                            <Dropdown
                                                width="150px"
                                                height="35px"
                                                value={consent ? consent.global_id : ''}
                                                values={[
                                                    {
                                                        text: !consent ? 'Select Consent' : 'None',
                                                        value: '',
                                                        disabled: false,
                                                    },
                                                    ...consents.map((consent) => ({
                                                        text: consent.name,
                                                        value: consent.global_id,
                                                    })),
                                                ]}
                                                setValue={(e) => {
                                                    updateSelectedConsent(e.target.value);
                                                }}
                                            />
                                            {consent &&
                                                consent.primary_table_global_id !==
                                                    primaryTableId && (
                                                    <>
                                                        <IconButton
                                                            padding="5px"
                                                            tooltip="Select Path for Consent"
                                                            className="consent-join-btn"
                                                            onClick={() =>
                                                                setIsConsentJoinPathModalOpen(true)
                                                            }
                                                        >
                                                            <RouteIcon
                                                                height={20}
                                                                width={20}
                                                                fill="#fff"
                                                            />
                                                        </IconButton>
                                                        <SelectTableJoinPathModal
                                                            isOpen={isConsentJoinPathModalOpen}
                                                            setOpen={setIsConsentJoinPathModalOpen}
                                                            tableProperty={consentTableProperty}
                                                        />
                                                    </>
                                                )}
                                        </>
                                    )}
                                </div>
                                <div className="action-btn-group action-settings">
                                    {segment_id !== null && (
                                        <div className="results-count">
                                            Results Count:{' '}
                                            {!resultsRunning && resultsCount !== null ? (
                                                resultsCount.toLocaleString()
                                            ) : (
                                                <Loader size={30} />
                                            )}
                                        </div>
                                    )}
                                    {segment_id && (
                                        <>
                                            {groupedFlows &&
                                                hasPermissionFor(
                                                    permissions,
                                                    'segmentation.flow.*.*',
                                                    'update'
                                                ) &&
                                                groupedFlows.map((groupedFlow, i) => (
                                                    <DynamicAppLogoButton
                                                        segmentId={segment_id}
                                                        app={groupedFlow.app}
                                                        connection={groupedFlow.connection}
                                                        flows={groupedFlow.routines}
                                                        key={i}
                                                    />
                                                ))}
                                            {hasPermissionFor(
                                                permissions,
                                                'segmentation.flow.new.*',
                                                'create'
                                            ) && (
                                                <IconButton
                                                    onClick={() => {
                                                        if (
                                                            apps_loading ||
                                                            segment_app_routines_loading
                                                        ) {
                                                            return openModal('flowsLoading');
                                                        } else if (
                                                            connected_apps_error ||
                                                            segment_app_routines_error
                                                        ) {
                                                            return openModal('flowsLoadingError');
                                                        }
                                                        navigate(
                                                            `/segmentation/flow/new/${segment_id}`
                                                        );
                                                    }}
                                                    tooltip="Create Flow"
                                                >
                                                    <PlayIcon
                                                        fill={
                                                            apps_loading || connected_apps_error
                                                                ? 'var(--greyscale-highlight-color)'
                                                                : 'var(--font-color)'
                                                        }
                                                        height="24"
                                                        width="24"
                                                    />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                onClick={() =>
                                                    navigate(`/segmentation/download/${segment_id}`)
                                                }
                                                tooltip="Export CSV"
                                            >
                                                <DownloadIcon
                                                    fill="var(--font-color)"
                                                    height="24"
                                                    width="24"
                                                />
                                            </IconButton>
                                            {canDelete(permissionSet) && (
                                                <IconButton
                                                    tooltip="Delete"
                                                    onClick={() => openModal('delete')}
                                                >
                                                    <DeleteIcon
                                                        fill="var(--error-color)"
                                                        height="24"
                                                        width="24"
                                                    />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                    <IconButton
                                        tooltip="Exit"
                                        onClick={() => {
                                            openModal('cancel');
                                        }}
                                    >
                                        <BackIcon height="24" width="24" />
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                    </ContentHeader>
                    <Modal
                        options={getModalOptions(modalType)}
                        isOpen={modalIsOpen}
                        setOpen={setIsOpen}
                    />
                    {!isPreview ? (
                        <div className="segment-body">
                            <DndProvider backend={HTML5Backend}>
                                <SegmentDragLayer />
                                <div className="segment-table-list">
                                    <SideMenu header="Fields" width="100%" minHeight="560px">
                                        <Accordion
                                            items={tablePropertyItemsForAccordion}
                                            loading={loadingTableProperties}
                                            padding="10px"
                                        />
                                    </SideMenu>
                                </div>
                                <div className="segment-builder-container" ref={segmentBuilderRef}>
                                    {isLoadingSegmentBuilder ||
                                    isLoadingSegment ||
                                    loadingTableProperties ? (
                                        <div className="segment-builder-loader">
                                            <Loader />
                                        </div>
                                    ) : (
                                        <div
                                            className={`segment-builder ${
                                                isSegmentBuilderFixed ? 'fixed' : ''
                                            }`}
                                            style={{
                                                width: segmentBuilderWidth,
                                                left: segmentBuilderLeftPos,
                                            }}
                                        >
                                            {errorMsgs?.length > 0 && (
                                                <div className="segment-errors">
                                                    {errorMsgs.map((msg, i) => (
                                                        <Alert key={i} variant="error">
                                                            {msg}
                                                        </Alert>
                                                    ))}
                                                </div>
                                            )}
                                            <div
                                                className={`include filter-group-card${
                                                    includeCollapsed ? ' collapsed' : ''
                                                }`}
                                            >
                                                <div
                                                    className="vertical-header"
                                                    onClick={() =>
                                                        setIncludeCollapsed((prev) => !prev)
                                                    }
                                                >
                                                    <span className="vertical-header-title">
                                                        Include
                                                    </span>
                                                </div>
                                                <div
                                                    className="overlay"
                                                    onClick={() =>
                                                        setIncludeCollapsed((prev) => !prev)
                                                    }
                                                ></div>
                                                <div className="drop-zone">
                                                    <FilterGroup
                                                        filterGroup={includeFilterGroup}
                                                        updateFilterGroup={(options) => {
                                                            updateFilterGroup('include', options);
                                                        }}
                                                        callUpdateFilterGroupOnHover={
                                                            callUpdateFilterGroupOnHover
                                                        }
                                                        triggerClearAllPlaceholders={
                                                            triggerClearAllPlaceholders
                                                        }
                                                        isCombinePreviewActive={
                                                            isCombinePreviewActive
                                                        }
                                                        setIsCombinePreviewActive={
                                                            setIsCombinePreviewActive
                                                        }
                                                        primaryTableId={primaryTableId}
                                                        primaryTableTree={primaryTableTree}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className={`exclude filter-group-card${
                                                    excludeCollapsed ? ' collapsed' : ''
                                                }`}
                                            >
                                                <div
                                                    className="vertical-header"
                                                    onClick={() =>
                                                        setExcludeCollapsed(!excludeCollapsed)
                                                    }
                                                >
                                                    <span className="vertical-header-title">
                                                        Exclude
                                                    </span>
                                                </div>
                                                <div
                                                    className="overlay"
                                                    onClick={() =>
                                                        setExcludeCollapsed(!excludeCollapsed)
                                                    }
                                                ></div>
                                                <div className="drop-zone">
                                                    <FilterGroup
                                                        filterGroup={excludeFilterGroup}
                                                        updateFilterGroup={(options) => {
                                                            updateFilterGroup('exclude', options);
                                                        }}
                                                        callUpdateFilterGroupOnHover={
                                                            callUpdateFilterGroupOnHover
                                                        }
                                                        triggerClearAllPlaceholders={
                                                            triggerClearAllPlaceholders
                                                        }
                                                        isCombinePreviewActive={
                                                            isCombinePreviewActive
                                                        }
                                                        setIsCombinePreviewActive={
                                                            setIsCombinePreviewActive
                                                        }
                                                        primaryTableId={primaryTableId}
                                                        primaryTableTree={primaryTableTree}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </DndProvider>
                        </div>
                    ) : (
                        <SegmentPreview
                            segmentId={segment_id}
                            segmentType={segmentType}
                            primaryTable={joinTables.find((t) => t.global_id === primaryTableId)}
                        ></SegmentPreview>
                    )}
                </div>
            )}
        </div>
    );
};

export default Segment;
